/**
 * Map over items with at most `limit` promises in flight at once, preserving
 * input order in the results. Used to cap per-vault API fan-outs so a large
 * org doesn't fire dozens of parallel requests and trip the relay's per-IP
 * rate limit. Rejections propagate — pass an fn that catches its own errors
 * when partial results are acceptable.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  const queue = items.map((item, index) => ({ item, index }));
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (let entry = queue.shift(); entry !== undefined; entry = queue.shift()) {
      results[entry.index] = await fn(entry.item, entry.index);
    }
  });
  await Promise.all(workers);
  return results;
}
